import { createSlice } from '@reduxjs/toolkit';

import { COE_SLICE_NAME } from '../slicesNames';
import { type CoeVendorWithPrograms, type CoeProgramWithVendor, type CoeState } from './coe.types';
import * as appActions from '../../appActions';

const initialState: CoeState = {
  vendors: {},
  programs: {},
  _network: null, // this state is here for debugging purposes only, as an easy way to see which networks COE's have been updated for
};

const coeSlice = createSlice({
  name: COE_SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(appActions.networkChanged, (state, action) => {
      const { newNetwork } = action.payload;

      if (!newNetwork || !newNetwork?.config) {
        return { ...initialState };
      }

      state.vendors = {};
      state.programs = {};
      state._network = newNetwork.slug;

      const coeVendors = newNetwork.config.coeVendors ?? [];
      const coePrograms = newNetwork.config.coePrograms ?? [];

      for (const vendor of coeVendors) {
        const vendorWithPrograms: CoeVendorWithPrograms = { ...vendor, programs: [] };
        state.vendors[vendor.id] = vendorWithPrograms;
      }

      for (const program of coePrograms) {
        const parentVendor = state.vendors[program.vendorId] || null;

        const programWithVendor: CoeProgramWithVendor = {
          ...program,
          // there should always be a parent vendor, but there is nothing client side to enforce that.
          // in order to be defensive about this, it's initialized as null and if a parent vendor id
          // is matched, it is assigned below
          vendor: null,
        };

        if (parentVendor) {
          const { programs: _programs, ...plainVendor } = parentVendor;
          programWithVendor.vendor = plainVendor; // add the vendor without the nested programs array

          parentVendor.programs.push(program);
        }

        state.programs[program.id] = programWithVendor;
      }

      return state;
    });
  },
});

export default coeSlice;
